import { GroupBooking, VenueRequestFile, VenueRequestStatus } from '@go-shared/types/groupBooking';
import { BareSimpleSailingDayBundle } from '@go-shared/types/sailing';
import { VenueConfiguration, VenueTemplate } from '@go-shared/types/venueTemplate';
import React, { useState } from 'react';

import {
  BubbleAlert,
  BubbleBadge,
  BubbleButton,
  BubbleFormGroup,
  BubbleInput,
  BubbleSelect,
} from '../BubbleUI';
import BubbleFileM from '../BubbleUI/BubbleFileM';
import { FormGenericContainer } from '../FormGenericContainer';
import { BookingForm, GroupForm } from './Workflows';
import FormStatusFooter from './FormStatusFooter';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { hashStringToColor, safeIdCompare } from '@go-shared/lib/utils';
import { hasVenueHoursConflict } from '@go-shared/lib/plan';
import { DEFAULT_DAY_START } from '@go-shared/lib/property';
import { calculateTimeDifference, calculateTimeOffset, timeToMinutes } from '@go-shared/lib/time';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { canBookRequestsBypassingTimeRestrictions } from '@go-shared/lib/permissions';
import TinyEditor from '../TinyEditor';
import { GroupResource } from '@go-shared/types/zod/groupResource';
import { useTotalFunction } from '@go-client/hooks/useTotalFunction';
import AddResourceForm from './AddResourceForm';
import { BrandGroupResourceType } from '@go-shared/types/brand';
import { getCommonTexts } from '@go-shared/lib/brand';
import { useServerInfo } from '@go-client/context/ServerInfoContext';

interface EnterBookingDetailsPageProps {
  bookingForm: BookingForm;
  changeBookingForm: <T extends keyof BookingForm>(key: T, val: BookingForm[T]) => void;
  clearBookingForm?: () => void;
  venueConfigOpts: VenueConfiguration[];
  groupResources: GroupResource[];
  groupResourcesTypes: BrandGroupResourceType[];
  title?: string;
  pageIndex?: number;
  isNewGroup: boolean;
  groupForm: GroupForm;
  previousStatus?: VenueRequestStatus;
  selectedGroup?: GroupBooking;
  selectedSailingBundle?: BareSimpleSailingDayBundle;
  selectedVenue: VenueTemplate;
  showDayContext?: boolean;
  hideDayContextButton?: boolean;
  hideDraft?: boolean;
  refreshing?: boolean;
  submitTitle?: string;
  onGoBack?: () => void;
  onGoForward?: () => void;
  onClose?: () => void;
  onToggleShowDayContext?: () => void;
  onSubmitBookingForm: (status?: VenueRequestStatus) => Promise<void>;
  onUpdateFile: (evt: VenueRequestFile) => void;
  onUploadFile: (file: File) => void;
  onDeleteFile: (file: VenueRequestFile) => void;
}

export default function EnterBookingPage(props: EnterBookingDetailsPageProps) {
  const { activeProperty } = useActiveProperty();
  const { user } = useUserInfo();
  const { functionTotal } = useTotalFunction(props.bookingForm.resources);
  const [isBusy, setIsBusy] = useState(false);

  const { brand } = useServerInfo();
  const { group_booking, guests } = getCommonTexts(
    {
      default: brand.commonTexts,
      translations: [],
    },
    { selected: 'en' }
  );

  const groupResourcesOpt = props.groupResources.map(r => ({
    ...r,
    archived: false,
    amount: 0,
    resourceId: r._id,
  }));

  async function handleOnSubmitGroupBooking(status?: VenueRequestStatus) {
    const statusToUse =
      props.previousStatus === 'approved' ? 'modified' : status || props.previousStatus;

    setIsBusy(true);

    try {
      if (props.onSubmitBookingForm) await props.onSubmitBookingForm(statusToUse);
      if (props.onGoForward) props.onGoForward();
    } finally {
      setIsBusy(false);
    }
  }

  const formValid =
    props.bookingForm.name.length > 0 &&
    props.bookingForm.startTime &&
    props.bookingForm.endTime &&
    props.bookingForm.duration >= 15;

  const meetsTimeBoundary =
    !hasVenueHoursConflict(
      props.selectedVenue.bookingHours,
      props.bookingForm.startTime,
      props.bookingForm.endTime,
      timeToMinutes(activeProperty.dayStart || DEFAULT_DAY_START)
    ) || canBookRequestsBypassingTimeRestrictions(user);

  const canSubmit = !isBusy && formValid && meetsTimeBoundary;

  return (
    <div className="tflf-3">
      <FormGenericContainer
        footer={() => (
          <>
            <FormStatusFooter
              {...props}
              functionTotal={functionTotal}
              className="text-slate-dark mr-auto"
            />
            {props.onGoBack && (
              <BubbleButton variant="navy" inverse onClick={props.onGoBack} disabled={isBusy}>
                Go Back
              </BubbleButton>
            )}
            {!props.hideDraft && (
              <BubbleButton
                variant="banana"
                className="mr-3"
                disabled={!canSubmit}
                onClick={() => handleOnSubmitGroupBooking('draft')}
              >
                Save as Draft
              </BubbleButton>
            )}
            <BubbleButton
              variant="navy"
              disabled={!canSubmit}
              onClick={() => handleOnSubmitGroupBooking()}
            >
              {props.submitTitle || 'Finish: Submit Group Booking Request'}
            </BubbleButton>
          </>
        )}
      >
        <div className="flex items-start mb-5">
          <div className="mr-auto">
            <div className="flex items-center">
              <h1
                className="text-lg text-slate-dark font-semibold"
                dangerouslySetInnerHTML={{ __html: props.title || 'Create Group Booking' }}
              />
              <BubbleBadge color={hashStringToColor(activeProperty.name)} className="ml-3">
                {activeProperty.name}
              </BubbleBadge>
            </div>
            <h2 className="text-2xl text-navy">
              {props.pageIndex !== undefined ? `${props.pageIndex}. ` : ''}Group Event Details
            </h2>
          </div>
          <div className="ml-auto" />
          <BubbleButton
            variant="navy"
            inverse
            onClick={props.onClose}
            icon="xmark-large"
            iconSize="xl"
          />
        </div>
        <div>
          {props.clearBookingForm && (
            <div className="-my-5 text-right relative">
              <BubbleButton variant="smoke" inverse onClick={props.clearBookingForm} icon="eraser">
                Clear Form
              </BubbleButton>
            </div>
          )}
          <div className="grid grid-cols-3 gap-4 relative">
            <BubbleFormGroup label={group_booking} className="col-span-3" asterisk>
              <BubbleInput
                block
                type="text"
                value={props.bookingForm.name}
                onValue={val => props.changeBookingForm('name', val)}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Start Time" className="flex-grow" asterisk>
              <BubbleInput
                block
                type="time"
                value={props.bookingForm.startTime}
                onValue={val => {
                  props.changeBookingForm('startTime', val);
                  if (val && props.bookingForm.endTime) {
                    const newDuration = calculateTimeDifference(props.bookingForm.endTime, val);
                    props.changeBookingForm('duration', newDuration);
                  }
                }}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="End Time" className="flex-grow" asterisk>
              <BubbleInput
                block
                type="time"
                value={props.bookingForm.endTime}
                onValue={val => {
                  props.changeBookingForm('endTime', val);
                  if (val && props.bookingForm.startTime) {
                    const newDuration = calculateTimeDifference(val, props.bookingForm.startTime);
                    props.changeBookingForm('duration', newDuration);
                  }
                }}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label={`${guests} (min. 0)`} className="flex-grow">
              <BubbleInput
                block
                type="number"
                min="0"
                value={props.bookingForm.numGuests}
                onValue={val => props.changeBookingForm('numGuests', val)}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Duration (minutes)" className="flex-grow">
              <BubbleInput
                block
                type="number"
                min="0"
                value={props.bookingForm.duration}
                onValue={val => {
                  props.changeBookingForm('duration', val);
                  //update endTime
                  if (props.bookingForm.startTime) {
                    const { time } = calculateTimeOffset(props.bookingForm.startTime, val);
                    props.changeBookingForm('endTime', time);
                  }
                }}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Set Up Time (minutes)" className="flex-grow">
              <BubbleInput
                block
                type="number"
                min="0"
                value={props.bookingForm.bufferTime.setUpTime}
                onValue={val =>
                  props.changeBookingForm('bufferTime', {
                    ...props.bookingForm.bufferTime,
                    setUpTime: val,
                  })
                }
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Breakdown Time (minutes)" className="flex-grow">
              <BubbleInput
                block
                type="number"
                min="0"
                value={props.bookingForm.bufferTime.breakdownTime}
                onValue={val =>
                  props.changeBookingForm('bufferTime', {
                    ...props.bookingForm.bufferTime,
                    breakdownTime: val,
                  })
                }
              />
            </BubbleFormGroup>

            <AddResourceForm
              resources={groupResourcesOpt}
              selectedResources={props.bookingForm.resources}
              resourceTypes={props.groupResourcesTypes}
              onUpdate={val => {
                const resourceIndex = props.bookingForm.resources.findIndex(r =>
                  safeIdCompare(r._id, val._id)
                );
                const updatedResources = [...props.bookingForm.resources];
                updatedResources[resourceIndex] = val;
                props.changeBookingForm('resources', updatedResources);
              }}
              onTrash={val => {
                const filteredResources = props.bookingForm.resources.filter(
                  r => !safeIdCompare(r._id, val._id)
                );
                props.changeBookingForm('resources', filteredResources);
              }}
              onCreate={val =>
                props.changeBookingForm('resources', [val, ...(props.bookingForm.resources || [])])
              }
              onAdd={val =>
                props.changeBookingForm('resources', [...(props.bookingForm.resources || []), val])
              }
            />

            <BubbleFormGroup label="Venue Configuration" className="col-span-3">
              <BubbleSelect
                block
                allowUndefined
                labelExtractor={c => `${c.name} (occ. ${c.occupancy})`}
                options={props.venueConfigOpts}
                value={props.bookingForm.venueConfiguration}
                onValue={val => props.changeBookingForm('venueConfiguration', val)}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Group Event Notes" className="col-span-3">
              <TinyEditor
                value={props.bookingForm.notes}
                onValue={val => props.changeBookingForm('notes', val)}
                mode="text"
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Internal Notes" className="col-span-3">
              <TinyEditor
                value={props.bookingForm.internalNotes}
                onValue={val => props.changeBookingForm('internalNotes', val)}
                mode="text"
              />
            </BubbleFormGroup>
            <div className="col-span-3">
              <BubbleFileM
                label="Group Event Files"
                files={props.bookingForm.files}
                onUpdateFile={props.onUpdateFile}
                onUploadFile={props.onUploadFile}
                onDeleteFile={props.onDeleteFile}
              />
            </div>
            <div className="col-span-3">
              {!meetsTimeBoundary && (
                <BubbleAlert variant="banana">
                  To submit this booking, its time must be within open bookings hours as specified
                  on the venue.
                </BubbleAlert>
              )}
            </div>
          </div>
        </div>
      </FormGenericContainer>
    </div>
  );
}
